import { useCallback } from "react";
import { type ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  AddIssuePhotoFieldValueDocument,
  ClearIssueCustomFieldDocument,
  RemoveIssuePhotoFieldValueDocument,
  UpdateIssueAddressDocument,
  UpdateIssueAssigneesDocument,
  UpdateIssueDescriptionDocument,
  UpdateIssueDocument,
  UpdateIssueStageDocument,
  UpdateIssueTeamsDocument,
  UpdateIssueValuesDocument,
  UpdateIssueWorkflowDocument,
} from "#app/lib/data";
import {
  useTrackedMutation,
  payloadIs,
  AnalyticsContext,
  IssueEvent,
  AnalyticsView,
} from "#app/lib/analytics";
import { createEditSessionGate } from "#app/lib/analytics/edit-session";
import { identifyIssueInCache } from "#app/hooks/kanban/kanban-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { holdPhotoField } from "#app/lib/photo-field-holds";

export type IssueEditTracking = {
  /** The surface the edit came from: "card", "detail", "list_row",
   *  "table_cell", "widget", "widget_data", "map". */
  via?: string;
  view?: AnalyticsView;
  /** The label for the omnibus `updateIssue`, which serves several actions:
   *  "rename", "update_source", "change_type", "follow_up". */
  updateIssueLabel?: string;
};

// The shared `via` values of the edit hosts.
export const DETAIL_EDIT: IssueEditTracking = { via: "detail" };
export const CARD_EDIT: IssueEditTracking = { via: "card" };
export const TABLE_CELL_EDIT: IssueEditTracking = { via: "table_cell", view: AnalyticsView.List };
export const WIDGET_DATA_EDIT: IssueEditTracking = {
  via: "widget_data",
  view: AnalyticsView.WidgetView,
};

const issueEdit = (label: string, tracking: IssueEditTracking) =>
  ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.Edit,
    label,
    data: { via: tracking.via ?? "unknown", ...(tracking.view ? { view: tracking.view } : {}) },
  }) as const;

// `updateIssue` answers with a result union; a typed error still resolves.
const updatedIssue = payloadIs("updateIssue", "Issue");

// The description autosaves on a debounce, so one editing session sends many
// writes. Count a session once: a write within this window of the last one
// on the same issue is the same session.
const descriptionSession = createEditSessionGate(5 * 60_000);

/** True for the first successful description write of an editing session. */
function startsDescriptionSession(result: {
  data?: { updateIssue?: { __typename?: string; issueId?: string } | null } | null | undefined;
}): boolean {
  const payload = result.data?.updateIssue;
  if (payload?.__typename !== "Issue" || !payload.issueId) return false;
  return descriptionSession(payload.issueId);
}

// Commutative cache write for a photos-field add/remove. Mutates the Issue's
// `values` map in place via cache.modify (the add/remove mutations deliberately
// don't return `values`, so this is the ONLY local writer). Because each call is a
// single-element delta — add dedupes, remove filters — it's idempotent and
// order-independent: optimistic layers stack cumulatively, and an out-of-order
// real response can't replace the field with a stale snapshot. Issue is keyed
// by `issueId` (see apollo-client.ts typePolicies).
function applyPhotoFieldDelta(
  cache: ApolloCache,
  args: { issueId: string; fieldId: string; photoId: string; op: "add" | "remove" },
): void {
  const cacheId = identifyIssueInCache(cache, args.issueId);
  if (!cacheId) return;

  cache.modify({
    id: cacheId,
    fields: {
      // Param typed `any`, mirroring connection-cache-utils — cache.modify's
      // Modifier signature doesn't accept a narrower object type here.
      values(existing: any) {
        const map: Record<string, unknown> = { ...(existing ?? {}) };
        const arr = Array.isArray(map[args.fieldId])
          ? (map[args.fieldId] as unknown[]).filter((v): v is string => typeof v === "string")
          : [];
        map[args.fieldId] =
          args.op === "add"
            ? arr.includes(args.photoId)
              ? arr
              : [...arr, args.photoId]
            : arr.filter((id) => id !== args.photoId);
        return map;
      },
    },
  });
}

/**
 * Centralizes all issue-view mutation hooks. Components destructure
 * only the mutations they need and pass their own variables/optimistic responses.
 *
 * Single-field renames (e.g. issue title) use the omnibus `updateIssue`
 * directly — it covers that path and returns the full IssueViewIssue response
 * shape.
 *
 * Photos fields are the exception to the "pass your own variables" rule:
 * `addPhotoFieldValue`/`removePhotoFieldValue` are higher-level helpers that
 * own their optimistic + commutative cache write, so concurrent uploads can't
 * clobber each other the way a per-field full-array overwrite would.
 */
export function useIssueViewMutations(tracking: IssueEditTracking = {}) {
  const [updateDescriptionMut] = useMutation(UpdateIssueDescriptionDocument);
  const [updateWorkflowMut] = useMutation(UpdateIssueWorkflowDocument);
  const [updateStageMut] = useMutation(UpdateIssueStageDocument);
  const [updateAddressMut] = useMutation(UpdateIssueAddressDocument);
  const [updateAssigneesMut] = useMutation(UpdateIssueAssigneesDocument);
  const [updateTeamsMut] = useMutation(UpdateIssueTeamsDocument);
  const [updateValuesMut] = useMutation(UpdateIssueValuesDocument);
  const [clearCustomFieldMut] = useMutation(ClearIssueCustomFieldDocument);
  const [addPhotoMut] = useMutation(AddIssuePhotoFieldValueDocument);
  const [removePhotoMut] = useMutation(RemoveIssuePhotoFieldValueDocument);
  const [updateIssueMut] = useMutation(UpdateIssueDocument);

  const edit = (label: string) => issueEdit(label, tracking);
  const updateDescription = useTrackedMutation(
    updateDescriptionMut,
    edit("update_description"),
    startsDescriptionSession,
  );
  const updateWorkflow = useTrackedMutation(updateWorkflowMut, edit("update_workflow"), updatedIssue);
  const updateStage = useTrackedMutation(updateStageMut, edit("update_stage"), updatedIssue);
  const updateAddress = useTrackedMutation(updateAddressMut, edit("update_address"), updatedIssue);
  const updateAssignees = useTrackedMutation(updateAssigneesMut, edit("update_assignees"), updatedIssue);
  const updateTeams = useTrackedMutation(updateTeamsMut, edit("update_teams"), updatedIssue);
  const updateValues = useTrackedMutation(updateValuesMut, edit("update_values"), updatedIssue);
  const clearCustomField = useTrackedMutation(clearCustomFieldMut, edit("clear_custom_field"));
  const addPhotoTracked = useTrackedMutation(addPhotoMut, edit("add_photo_field_value"));
  const removePhotoTracked = useTrackedMutation(removePhotoMut, edit("remove_photo_field_value"));
  const updateIssue = useTrackedMutation(
    updateIssueMut,
    edit(tracking.updateIssueLabel ?? "update_issue"),
    updatedIssue,
  );

  const addPhotoFieldValue = useCallback(
    async (issueId: string, fieldId: string, photoId: string) => {
      const release = holdPhotoField(issueId, fieldId);
      try {
        await addPhotoTracked({
          variables: { issueId, fieldId, photoId },
          optimisticResponse: {
            addIssuePhotoFieldValue: {
              __typename: "Issue",
              id: `Issue:${issueId}`,
              issueId,
            },
          },
          update: (cache) => applyPhotoFieldDelta(cache, { issueId, fieldId, photoId, op: "add" }),
        });
      } catch (err) {
        toastMutationError(err, "Failed to add photo");
      } finally {
        release();
      }
    },
    [addPhotoTracked],
  );

  const removePhotoFieldValue = useCallback(
    async (issueId: string, fieldId: string, photoId: string) => {
      const release = holdPhotoField(issueId, fieldId);
      try {
        await removePhotoTracked({
          variables: { issueId, fieldId, photoId },
          optimisticResponse: {
            removeIssuePhotoFieldValue: {
              __typename: "Issue",
              id: `Issue:${issueId}`,
              issueId,
            },
          },
          update: (cache) => applyPhotoFieldDelta(cache, { issueId, fieldId, photoId, op: "remove" }),
        });
      } catch (err) {
        toastMutationError(err, "Failed to remove photo");
      } finally {
        release();
      }
    },
    [removePhotoTracked],
  );

  return {
    updateDescription,
    updateWorkflow,
    updateStage,
    updateAddress,
    updateAssignees,
    updateTeams,
    updateValues,
    clearCustomField,
    addPhotoFieldValue,
    removePhotoFieldValue,
    updateIssue,
  };
}
